import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, X } from "lucide-react";
import { createPortal } from "react-dom";

import { MENU_ITEMS } from "shared/utils/constants";
import { useAuthStore } from "app/store/auth.store";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigationContext } from "shared/providers/NavigationProvider";
import { useNotification } from 'shared/providers/NotificationProvider';
import { useTranslation } from 'react-i18next';
import { isAdminHost } from "shared/utils/host-routing";

const getMenuItems = (isAuthenticated, adminHost) => {
    if (!adminHost) {
        return MENU_ITEMS.filter(item => item.visibility === "public" && item.labelKey !== "menu.signIn");
    }

    if (isAuthenticated) {
        return MENU_ITEMS.filter(item => (item.visibility !== "public"));
    } else {
        return MENU_ITEMS.filter(item => (item.visibility !== "private"));
    }
};

const MenuOverlay = ({ isOpen, onClose }) => {
    
    const { t } = useTranslation();
    const { isAuthenticated, setIsAuthenticated } = useAuthStore();
    const adminHost = isAdminHost();

    const menuItems = useMemo(() => getMenuItems(isAuthenticated, adminHost), [adminHost, isAuthenticated]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const previousBodyOverflow = document.body.style.overflow;
        const previousDocumentOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousDocumentOverflow;
        };
    }, [isOpen]);

    if (typeof document === "undefined") {
        return null;
    }

    const overlay = (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    role="dialog"
                    aria-modal="true"
                    aria-label={t("menu.title", "Menu")}
                    className="fixed inset-0 z-[1000] flex min-h-[100dvh] flex-col overflow-y-auto overscroll-contain bg-white"
                >
                    <div className="relative flex min-h-full flex-1 flex-col items-center justify-center gap-8 px-4 py-20">
                        <button onClick={onClose} className="absolute right-6 top-6 rounded-full bg-gray-100 p-2 hover:text-lime-700 lg:right-36 lg:top-6">
                            <X className="w-6 h-6" />
                        </button>
                        <div className="flex w-full max-w-[350px] items-center justify-between px-4 pb-5 text-xl font-medium text-gray-800 transition lg:max-w-[500px]">
                            <h1 className="text-5xl">Menu</h1>
                        </div>

                        {menuItems.map((item, index) => (
                            <MenuItem
                                key={item.labelKey}
                                item={item}
                                index={index}
                                onClose={onClose}
                                setIsAuthenticated={setIsAuthenticated}
                                t={t}
                            />
                        ))}
                    </div>

                    <div className="shrink-0 pb-6 text-center text-sm text-gray-400">
                        © {new Date().getFullYear()} Meets&Works — {t('menu.allRightsReserved')}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return createPortal(overlay, document.body);
};

const MenuItem = ({ item, index, onClose, setIsAuthenticated, t }) => {
    
    const { notify } = useNotification();
    
    const { navigateToRouteWithState } = useNavigationContext();

    const handleNavigateToRouteWithState = useCallback(
        () => {
            // Close the modal, first:
            onClose();

            if (item.labelKey === "menu.signOut") {
                setIsAuthenticated(false);
                notify.success("You have been signed out.");
            }
            
            // Then navigate to the route with state:
            navigateToRouteWithState({
                path: item.section ? `/#${item.section}` : item.path,
                state: { scrollTo: item.section }
            })
        },
        [item.labelKey, item.path, item.section, navigateToRouteWithState, notify, onClose, setIsAuthenticated]
    );

    return (
        <motion.button
            key={item.labelKey}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.05 * index }}
            onClick={handleNavigateToRouteWithState}
            className="
                w-full max-w-[350px] lg:max-w-[500px]
                flex items-center justify-between
                text-xl font-medium
                text-gray-800
                hover:text-lime-700
                transition 
                px-4 py-0
            "
        >
            <span>{t(item.labelKey)}</span>
            <ChevronRight className="w-5 h-5" />
        </motion.button>
    );
};

export default MenuOverlay;
